import React from 'react';
import { ADMIN_ROUTES } from 'constants/routes';
import { Link } from 'react-router-dom';
import { ContactInfoFieldKeyType } from 'typings/form';
import { useContactPersons } from './useContactPersons';
import { OutlineArrowDownIcon } from 'components/common/icons';
import { ContactFieldRow } from 'components/common/ContactFieldRow';
import {
  Button,
  Col,
  ContactPersonDrawer,
  IconWrapper,
  MultiLineEllipsisText,
  PriorityBadge,
  Row,
  Skeleton,
  Spin,
  Tag,
  Typography,
} from 'components';
import styles from './styles.module.scss';
import detailsStyles from '../styles.module.scss';

export const ContactPersons = () => {
  const {
    dealDetails,
    onDrawerOpen,
    onDrawerClose,
    openDrawer,
    clientDetails,
    contactPersons,
    isLoading,
    toggleExpandPerson,
    isPersonExpanded,
  } = useContactPersons();

  if (isLoading) {
    return (
      <Row>
        <Col span={24}>
          <Skeleton active />
        </Col>
      </Row>
    );
  }

  // Display only the first contact person in the main view
  const mainContactPerson = contactPersons[0];
  const contactEntries = mainContactPerson?.contacts
    ? (Object.entries(mainContactPerson.contacts) as [ContactInfoFieldKeyType, string[]][])
    : [];

  return (
    <>
      <Col span={24} className={detailsStyles.main_info}>
        <Spin spinning={isLoading}>
          <Row>
            <Typography.Text className={'font-weight-600 font-size-16 margin-b-12'} style={{ color: '#05796B' }}>
              Client Information
            </Typography.Text>
          </Row>
          <Row justify={'space-between'} wrap={false}>
            <Col flex={'auto'}>
              <Link to={`${ADMIN_ROUTES.clients}/${clientDetails.clientId}/general`} target={'_blank'}>
                <Typography.Text underline ellipsis={{ tooltip: true }} className={styles.title}>
                  {clientDetails?.clientName}
                </Typography.Text>
              </Link>
            </Col>
            {clientDetails?.clientType && (
              <Col flex={'none'}>
                <Typography.Text>
                  <Tag className={styles.option_tag}>{clientDetails.clientType?.name}</Tag>
                </Typography.Text>
              </Col>
            )}
          </Row>
          <Row style={{ marginBottom: 8 }} justify={'space-between'}>
            <PriorityBadge tooltip={clientDetails.reason} type={clientDetails.clientPriority} />
          </Row>

          <Row gutter={[0, 4]} className={styles.client_info}>
            <Col span={24} className={styles.item_wrapper}>
              <Row justify={'space-between'}>
                <Col>
                  <Typography.Text type={'secondary'}>Average Margin</Typography.Text>
                </Col>
                <Col>
                  <Typography.Text type={'secondary'}>
                    {clientDetails?.averageMargin || 'Not Available'}
                  </Typography.Text>
                </Col>
              </Row>
            </Col>
            <Col span={24} className={styles.item_wrapper}>
              <Row justify={'space-between'}>
                <Col>
                  <Typography.Text type={'secondary'}>Conversion Rate</Typography.Text>
                </Col>
                <Col>
                  <Typography.Text type={'secondary'}>
                    {clientDetails?.conversionRate || 'Not Available'}
                  </Typography.Text>
                </Col>
              </Row>
            </Col>
            <Col span={24} className={styles.item_wrapper}>
              <Row justify={'space-between'}>
                <Col>
                  <Typography.Text type={'secondary'}>Orders Count</Typography.Text>
                </Col>
                <Col>
                  <Typography.Text type={'secondary'}>{clientDetails?.ordersCount || 'Not Available'}</Typography.Text>
                </Col>
              </Row>
            </Col>
            <Col span={24} className={styles.item_wrapper}>
              <Row justify={'space-between'}>
                <Col>
                  <Typography.Text type={'secondary'}>Turnover</Typography.Text>
                </Col>
                <Col>
                  <Typography.Text type={'secondary'}>{clientDetails?.turnover || 'Not Available'}</Typography.Text>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row>
                {clientDetails.tags?.map((_tag: string, index: number) => (
                  <Tag style={{ marginRight: 5 }} title={_tag} key={index + _tag} className={'custom-tag'}>
                    {_tag}
                  </Tag>
                ))}
              </Row>
            </Col>
            <Col span={24}>
              <MultiLineEllipsisText subTitle={'Additional Notes:'} text={dealDetails?.client?.additionalNotes} />
            </Col>
          </Row>

          {contactPersons.length > 0 && (
            <>
              <Row style={{ marginTop: 16 }}>
                <Col span={24}>
                  <Typography.Text className={styles.title}>Contact Person</Typography.Text>
                </Col>
              </Row>

              <Row gutter={[0, 4]} className={styles.contact_persons}>
                <Col span={24}>
                  <Row justify={'space-between'} align={'middle'}>
                    <Col>
                      <Row className={styles.name_flag_container} align={'middle'}>
                        <Typography.Text type={'secondary'}>{mainContactPerson.fullName}</Typography.Text>
                        {mainContactPerson.language?.flagLocation && (
                          <IconWrapper src={mainContactPerson.language?.flagLocation} />
                        )}
                      </Row>
                    </Col>
                  </Row>
                </Col>

                {mainContactPerson.department && (
                  <Col span={24}>
                    <Row justify={'space-between'}>
                      <Col>
                        <Typography.Text type={'secondary'}>Department</Typography.Text>
                      </Col>
                      <Col>
                        <Typography.Text type={'secondary'}>{mainContactPerson.department}</Typography.Text>
                      </Col>
                    </Row>
                  </Col>
                )}

                {mainContactPerson.role && (
                  <Col span={24}>
                    <Row justify={'space-between'}>
                      <Col>
                        <Typography.Text type={'secondary'}>Role</Typography.Text>
                      </Col>
                      <Col>
                        <Typography.Text type={'secondary'}>{mainContactPerson.role}</Typography.Text>
                      </Col>
                    </Row>
                  </Col>
                )}

                {/* Display up to 3 contact methods by default */}
                {contactEntries.slice(0, 3).map(([type, values]) => (
                  <ContactFieldRow key={type} fieldValueMaxWidth={150} type={type} data={values} />
                ))}

                {/* "See All" button at the bottom if there are multiple contact persons */}
                {contactPersons.length > 1 && (
                  <Col span={24}>
                    <Row align={'middle'} justify={'end'}>
                      <Col>
                        <Button
                          type={'link'}
                          variant={'link'}
                          className={styles.see_all_button}
                          onClick={() => onDrawerOpen()}
                        >
                          See All
                          <OutlineArrowDownIcon size={'sm'} color={'#308D82'} />
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                )}
              </Row>
            </>
          )}
        </Spin>
      </Col>

      {/* Drawer to show all contact persons */}
      <ContactPersonDrawer
        visible={openDrawer}
        onClose={onDrawerClose}
        contactPersons={contactPersons}
        toggleExpandPerson={toggleExpandPerson}
        isPersonExpanded={isPersonExpanded}
      />
    </>
  );
};
